import { Fragment, useEffect, useState } from "react";
import { useVisibleInterval } from "../lib/polling";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, Tooltip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, Button, MenuItem, TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { fmtArDateTime } from "../services/monitoring";
import { fmtDuration, fmtUptime } from "../services/monitoreoEnlaces";
import {
  getProveedoresEstado, getProveedoresHistorial, evidenciaTexto,
  ProveedoresEstado, ProveedoresHistorial, OperacionEstado, OperacionHistorial, EstadoOp,
} from "../services/monitoreoProveedores";

const C_OK = "#16A34A", C_WARN = "#B45309", C_DOWN = "#DC2626", C_UNK = "#94A3B8";
const ESTADO: Record<EstadoOp, [string, string, string]> = {
  sano:        [C_OK,   "#DCFCE7", "Sano"],
  degradado:   [C_WARN, "#FEF3C7", "Degradado"],
  caido:       [C_DOWN, "#FEE2E2", "CAÍDO"],
  sin_trafico: [C_UNK,  "#F1F5F9", "Sin tráfico"],
};
const CRIT: Record<string, string> = { "crítica": "#B91C1C", alta: "#B45309", media: "#374151" };

function Dot({ color }: { color: string }) {
  return <Box component="span" sx={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", bgcolor: color, mr: 0.75, verticalAlign: "middle" }} />;
}

function EstadoChip({ estado, size = "small" }: { estado: EstadoOp; size?: "small" | "medium" }) {
  const [fg, bg, label] = ESTADO[estado] ?? ESTADO.sin_trafico;
  return <Chip label={label} size={size} sx={{ fontWeight: 700, fontSize: size === "small" ? 11 : 13, height: size === "small" ? 22 : 28, color: fg, bgcolor: bg }} />;
}

function UptimeStrip({ dias }: { dias: OperacionHistorial["dias"] }) {
  if (!dias.length) return <Typography variant="caption" color="text.secondary">Sin datos</Typography>;
  return (
    <Stack direction="row" spacing={0.25} sx={{ height: 22, alignItems: "stretch", minWidth: 180 }}>
      {dias.map((d) => {
        const color = d.uptime == null ? C_UNK : d.uptime >= 1 ? C_OK : d.uptime > 0.9 ? C_WARN : C_DOWN;
        const label = d.uptime == null ? "sin tráfico"
          : d.uptime >= 1 ? `sin cortes (${d.n} llamadas)` : `${fmtUptime(d.uptime)} — ${d.caido} ventana(s) caída(s) de ${d.ventanas}`;
        return (
          <Tooltip key={d.d} title={`${d.d}: ${label}`} arrow>
            <Box sx={{ flex: "1 1 0", minWidth: 2, bgcolor: color, borderRadius: 0.5, opacity: 0.85, "&:hover": { opacity: 1 } }} />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
}

function OperacionRow({ o, h, dias }: { o: OperacionEstado; h?: OperacionHistorial; dias: number }) {
  const [open, setOpen] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const errores15 = o.ult15.error_proveedor + o.ult15.timeout_nuestro;
  async function copiar() {
    if (!h) return;
    try { await navigator.clipboard.writeText(evidenciaTexto(h, dias)); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sin clipboard */ }
  }
  return (
    <Fragment>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <TableCell padding="checkbox">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.label}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace" }}>{o.op}</Typography>
        </TableCell>
        <TableCell><Typography variant="caption" sx={{ fontWeight: 700, color: CRIT[o.criticidad] ?? "#374151" }}>{o.criticidad}</Typography></TableCell>
        <TableCell><EstadoChip estado={o.estado} />{o.desde && o.estado !== "sano" && o.estado !== "sin_trafico" && (
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>desde {fmtArDateTime(o.desde)}</Typography>)}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          {o.ult15.n} <Typography component="span" variant="caption" color="text.secondary">llam.</Typography>
          {errores15 > 0 && <Typography component="span" variant="caption" sx={{ color: C_DOWN, fontWeight: 700 }}> · {errores15} err</Typography>}
          {o.ult15.limite > 0 && <Typography component="span" variant="caption" sx={{ color: C_WARN }}> · {o.ult15.limite} 429</Typography>}
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>p95 {fmtMs(o.ult15.p95)} (ref {fmtMs(o.p95_ref)})</Typography>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <strong>{fmtUptime(o.ult24.disponibilidad)}</strong>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>{o.ult24.n} llam. · {o.ult24.error_proveedor + o.ult24.timeout_nuestro} err · {o.ult24.caido} vent. caídas</Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 200 }}>
          {h && h.dias.length > 0 ? (
            <>
              <UptimeStrip dias={h.dias} />
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                {dias} d: <strong>{fmtUptime(h.uptime)}</strong>{h.minutos_caido ? ` · ${fmtDuration(h.minutos_caido * 60)} caído` : ""}
                {h.dias.length < dias ? ` · ${h.dias.length} día(s) con datos` : ""}
              </Typography>
            </>
          ) : <Typography variant="caption" color="text.secondary">Sin historial todavía</Typography>}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 2, py: 1.5, bgcolor: "#FAFAFA" }}>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ fontSize: 13, mb: 1 }}>
                <Box sx={{ minWidth: 260, flex: 1 }}><b>Quién la usa:</b> {o.consumidor}</Box>
                <Box sx={{ minWidth: 260, flex: 1 }}><b>Si falla:</b> {o.si_falla}</Box>
                <Box sx={{ minWidth: 260, flex: 1 }}><b>Respuesta sana:</b> {o.sana}</Box>
              </Stack>
              {Object.keys(o.ultimos_codigos).length > 0 && (
                <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                  Códigos de la última ventana ({o.ultima_ventana ? fmtArDateTime(o.ultima_ventana) : "—"}): {Object.entries(o.ultimos_codigos).map(([k, v]) => `${k}×${v}`).join(", ")}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>EPISODIOS DE CAÍDA ({dias} DÍAS)</Typography>
                {h && <Tooltip title={copiado ? "¡Copiado!" : "Copiar evidencia para el reclamo al proveedor"}>
                  <IconButton size="small" onClick={copiar}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>}
              </Stack>
              {!h || h.eventos.length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin episodios de caída en el período.</Typography>
              ) : (
                <Table size="small" sx={{ maxWidth: 900 }}>
                  <TableHead><TableRow>
                    <TableCell>Desde (AR)</TableCell><TableCell>Hasta (AR)</TableCell><TableCell align="right">Duración</TableCell>
                    <TableCell align="right">Errores / llamadas</TableCell><TableCell>Códigos</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {h.eventos.map((e) => (
                      <TableRow key={e.desde}>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtArDateTime(e.desde_iso)}</TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.en_curso ? "en curso" : fmtArDateTime(e.hasta_iso)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtDuration(e.duracion_s)}</TableCell>
                        <TableCell align="right">{e.errores} / {e.llamadas}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }} title={e.detalles.join("\n")}>
                          {Object.entries(e.codigos).map(([k, v]) => `${k}×${v}`).join(", ")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

export default function MonitoreoProveedores() {
  const [estado, setEstado] = useState<ProveedoresEstado | null>(null);
  const [hist, setHist] = useState<ProveedoresHistorial | null>(null);
  const [dias, setDias] = useState(30);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load(d = dias) {
    setLoading(true); setErr(null);
    try {
      const [e, h] = await Promise.all([getProveedoresEstado(), getProveedoresHistorial(d)]);
      setEstado(e); setHist(h);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(dias); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [dias]);
  useVisibleInterval(() => load(dias), 60_000);

  const histPor = new Map((hist?.operaciones ?? []).map((o) => [`${o.prov}#${o.op}`, o]));

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Proveedores: uptime funcional de sus APIs</Typography>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" select label="Historial" value={dias} onChange={(e) => setDias(Number(e.target.value))} sx={{ width: 140 }}>
          {[7, 30, 60, 90].map((d) => <MenuItem key={d} value={d}>{d} días</MenuItem>)}
        </TextField>
        <Tooltip title="Actualizar"><span><IconButton onClick={() => load(dias)} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
      </Stack>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Cuándo las APIs de los bancos y de Coelsa funcionan de verdad, medido sobre el tráfico real que ya pasa por nuestros
        connectors (ventanas de 5 minutos, sin llamadas de prueba). "Caído" es ≥ 50 % de errores del proveedor en la ventana
        (5xx, timeout, "servicio no disponible"); los 429 y los pedidos mal armados nuestros se cuentan aparte y no bajan el
        uptime. El aviso por mail (topic de enlaces bancarios) sale con 3 errores en 15 minutos o 3 ventanas lentas seguidas,
        y otro al recuperarse. Inventario y criterios: <code>docs/proveedores-apis.md</code> (CP-384).
        {estado?.ultima_corrida && <> Última medición: {fmtArDateTime(estado.ultima_corrida.ts)} (ventana hasta {estado.ultima_corrida.hasta.slice(11, 16)} UTC).</>}
      </Typography>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {estado?.ultima_corrida?.errores?.length ? (
        <Alert severity="warning" sx={{ mb: 2 }}>Fuentes con error en la última corrida: {estado.ultima_corrida.errores.join("; ")}</Alert>
      ) : null}
      {loading && !estado && <CircularProgress size={28} />}
      {estado?.proveedores.map((p) => {
        const [fg] = ESTADO[p.estado] ?? ESTADO.sin_trafico;
        return (
          <Paper key={p.key} variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, bgcolor: "#F8FAFC" }} flexWrap="wrap" useFlexGap>
              <Dot color={fg} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{p.label}</Typography>
              <EstadoChip estado={p.estado} size="medium" />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>fuente: {p.fuente} · {p.nota}</Typography>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Operación</TableCell>
                    <TableCell>Criticidad</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Últimos 15 min</TableCell>
                    <TableCell align="right">Últimas 24 h</TableCell>
                    <TableCell>Historial</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.operaciones.map((o) => <OperacionRow key={o.op} o={o} h={histPor.get(`${p.key}#${o.op}`)} dias={hist?.dias ?? dias} />)}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
      {estado && (
        <Button size="small" variant="text" onClick={() => load(dias)} disabled={loading}>Actualizar</Button>
      )}
    </Box>
  );
}
